import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCancelSubscription } from '@/api/service-settings';
import { trackFeatureAction } from '@/lib/analytics-events';

/** Pull a human message out of an axios-style error, with a sensible fallback. */
function errorMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

/**
 * Confirm dialog for cancelling a Kubernetes subscription. Archives the legacy
 * subscription and marks the @mgmt service archived. Blocked while clusters
 * are still active — the caller gates the open state on cluster count.
 */
export function KubernetesCancelSubscriptionDialog({
  open,
  onOpenChange,
  serviceId,
  serviceName,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  serviceName: string;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const cancel = useCancelSubscription(serviceId);

  const submit = () => {
    if (cancel.isPending) return;
    cancel.mutate(undefined, {
      onSuccess: () => {
        trackFeatureAction('kubernetes', 'service', 'disable');
        show({ tone: 'success', title: `Subscription cancelled for ${serviceName}` });
        onOpenChange(false);
        navigate('/services');
      },
      onError: (e) =>
        show({
          tone: 'danger',
          title: 'Could not cancel subscription',
          description: errorMessage(e, 'Cancellation failed — the subscription was left unchanged.'),
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel subscription for "{serviceName}"</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Alert tone="warning" title="This cancels the billing subscription">
              The legacy subscription will be archived and this service marked
              as cancelled. Existing data is preserved but the service can no
              longer be used. This action is recorded in the activity log.
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Keep subscription
          </Button>
          <Button variant="danger" disabled={cancel.isPending} onClick={submit}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
